/**
 * "Compact folders" for the flat tree-item list RepositoryTree renders (items
 * shaped `{ path, name, type: 'blob'|'tree', level, parent }`).
 *
 * A folder whose ONLY child is another folder is "absorbed": it is not drawn
 * itself, and the end of the chain is drawn with the joined label instead, so
 * `src/main/java/com/example/app` collapses into one row when nothing branches
 * off along the way (same behaviour as VS Code's "compact folders").
 *
 * The underlying items/paths are untouched — expand/collapse, selection,
 * drag-drop and "new file in folder" keep addressing the real (deepest)
 * folder path. Absorbed folders count as always-expanded for visibility.
 */
export function computeCompaction(tree) {
  const childrenOf = new Map();
  const byPath = new Map();
  for (const item of tree) {
    byPath.set(item.path, item);
    if (item.parent) {
      if (!childrenOf.has(item.parent)) childrenOf.set(item.parent, []);
      childrenOf.get(item.parent).push(item);
    }
  }

  const absorbed = new Set();
  for (const item of tree) {
    if (item.type !== 'tree') continue;
    const kids = childrenOf.get(item.path);
    if (kids && kids.length === 1 && kids[0].type === 'tree') absorbed.add(item.path);
  }

  const ancestorPaths = (path) => {
    const parts = path.split('/');
    const out = [];
    for (let i = 1; i < parts.length; i += 1) out.push(parts.slice(0, i).join('/'));
    return out;
  };

  return {
    absorbed,
    /** Row label: the item's own name, prefixed by the contiguous absorbed folders above it. */
    displayName(item) {
      if (item.type !== 'tree') return item.name;
      const names = [item.name];
      let parent = item.parent;
      while (parent && absorbed.has(parent)) {
        names.unshift(byPath.get(parent)?.name ?? parent.split('/').pop());
        parent = byPath.get(parent)?.parent;
      }
      return names.join('/');
    },
    /** Indentation level after removing the rows that were absorbed above it. */
    displayLevel(item) {
      const hidden = ancestorPaths(item.path).filter((p) => absorbed.has(p)).length;
      return Math.max(0, item.level - hidden);
    },
  };
}
